import { createAsyncThunk } from "@reduxjs/toolkit";
import { useQuery } from "@tanstack/react-query";
import { DateTime } from "luxon";

import { useEnergyForecastApi } from "@/auth/energyApi";
import type { EnergyForecastApi, EnergyForecastType, RequestInitDatesByModel } from "@mm/energy.metx.meteomatics.com";

export const getEnergyForecastAvailabilityV2 = createAsyncThunk(
  "api/energy-forecast/availability",
  async (payload: { energyForecastApi: EnergyForecastApi }, _thunkAPI) => {
    return await payload.energyForecastApi.getEnergyForecastAvailability();
  },
);

type UseEnergyForecastInitDatesByModelArgs = {
  forecast_type: EnergyForecastType;
  models: RequestInitDatesByModel["models"];
};

export type InitDateGroups = {
  today: number[];
  yesterday: number[];
};

export type NormalizedInitDatesByModelResponse = Record<string, InitDateGroups>;
export type EnergyForecastInitDatesByModelResponse = Record<string, number[]>;
type UseNormalizeInitDatesByModelResponseResult = {
  initDatesByModel: NormalizedInitDatesByModelResponse;
  error: Error | null;
  isLoading: boolean;
};

/** Narrows runtime strings to forecast types supported by the energy API. */
function isEnergyForecastType(value: string): value is EnergyForecastType {
  return value === "slp" || value === "wlp";
}

/** Creates the empty day buckets used for normalized init-date responses. */
function createEmptyInitDateGroups(): InitDateGroups {
  return {
    today: [],
    yesterday: [],
  };
}

/**
 * Groups model init dates into UTC today and yesterday buckets.
 * Invalid timestamps and dates outside those two buckets are ignored, and each
 * bucket is sorted oldest to newest for display.
 */
export function normalizeInitDatesByModelResponse(
  data: EnergyForecastInitDatesByModelResponse,
  referenceDay: DateTime = DateTime.utc().startOf("day"),
): NormalizedInitDatesByModelResponse {
  const today = referenceDay.startOf("day");
  const yesterday = today.minus({ days: 1 });

  return Object.entries(data).reduce<NormalizedInitDatesByModelResponse>((groupedResponse, [model, initDates]) => {
    const initDateGroups = createEmptyInitDateGroups();

    for (const initDate of initDates) {
      if (!Number.isFinite(initDate)) {
        continue;
      }

      const initDay = DateTime.fromSeconds(initDate, { zone: "utc" }).startOf("day");

      if (initDay.hasSame(today, "day")) {
        initDateGroups.today.push(initDate);
        continue;
      }

      if (initDay.hasSame(yesterday, "day")) {
        initDateGroups.yesterday.push(initDate);
      }
    }

    initDateGroups.today.sort((left, right) => left - right);
    initDateGroups.yesterday.sort((left, right) => left - right);

    groupedResponse[model] = initDateGroups;
    return groupedResponse;
  }, {});
}

/**
 * Fetches init-date availability and returns the selector-ready day buckets.
 * Consumers can render against an empty object while the query is disabled or
 * still loading.
 */
export function useNormalizeInitDatesByModelResponse({
  forecast_type,
  models,
}: UseEnergyForecastInitDatesByModelArgs): UseNormalizeInitDatesByModelResponseResult {
  const { data, error, isLoading } = useEnergyForecastInitDatesByModel({ forecast_type, models });

  return {
    initDatesByModel: normalizeInitDatesByModelResponse(data ?? {}),
    error,
    isLoading,
  };
}

/**
 * Loads raw init-date availability for the requested models.
 * The query remains disabled until an energy API client, at least one model,
 * and a supported forecast type are available.
 */
export function useEnergyForecastInitDatesByModel({ forecast_type, models }: UseEnergyForecastInitDatesByModelArgs) {
  const energyForecastApi = useEnergyForecastApi();
  const enabled = energyForecastApi !== null && models.length > 0 && isEnergyForecastType(forecast_type);

  return useQuery<EnergyForecastInitDatesByModelResponse, Error>({
    queryKey: ["energy-forecast", "init-dates-by-model", forecast_type, ...models],
    queryFn: async () => {
      if (!energyForecastApi) {
        throw new Error("Energy forecast API is unavailable");
      }

      return await energyForecastApi.getInitDatesByModel({
        requestInitDatesByModel: {
          forecast_type,
          models,
        },
      });
    },
    staleTime: 10 * 60 * 1000,
    enabled,
  });
}
